import { StringData } from "../..";
import { ISettingsChangeData } from "../../models/event/ISettingsChangeData";
import { IStringModule } from "../IStringModule";
import { StringModule } from "../StringModule";

type LifecycleName =
  | "destroy"
  | "onSubscribe"
  | "onUnsubscribe"
  | "onInit"
  | "onFrame"
  | "onMutate"
  | "onScrollMeasure"
  | "onMouseMoveMeasure"
  | "onScroll"
  | "onResizeWidth"
  | "onResize"
  | "onMouseMove"
  | "onWheel"
  | "onDirectionChange"
  | "onScrollStart"
  | "onScrollStop"
  | "onScrollMeasure"
  | "onMouseMoveMeasure"
  | "onScroll"
  | "onResizeWidth"
  | "onResize"
  | "onRebuild"
  | "onMouseMove"
  | "onWheel"
  | "onDirectionChange"
  | "onScrollStart"
  | "onScrollStop"
  | "onAxisChange"
  | "onDeviceChange"
  | "onScrollConfigChange"
  | "onSettingsChange"
  | "onDOMMutate";

export class ModuleManager {
  private modules: StringModule[] = [];
  private uiModules: StringModule[] = [];
  private allModules: StringModule[] = [];

  constructor(private data: StringData) { }

  register(module: StringModule): void {
    if (module.type === 1) {
      this.modules.push(module);
    } else if (module.type === 2) {
      this.uiModules.push(module);
    }

    if (module.cssProperties && module.cssProperties.length > 0) {
      if (typeof window.CSS !== "undefined" && "registerProperty" in window.CSS) {
        for (let i = 0; i < module.cssProperties.length; i++) {
          try {
            (window.CSS as any).registerProperty(module.cssProperties[i]);
          } catch (e) {
            // Property might already be registered by another instance of the same module type
          }
        }
      }
    }

    module.onSubscribe();
    this.rebuildAllModules();
  }

  find<T>(type: new (...args: any[]) => T): T | undefined {
    for (let i = 0; i < this.allModules.length; i++) {
      const module = this.allModules[i];
      if (module instanceof type) {
        return module as T;
      }
    }
    return undefined;
  }

  onInit(): void {
    this.callAll("onInit");
  }

  onFrame(): void {
    this.callAll("onFrame");
  }

  onMutate(): void {
    this.callAll("onMutate");
  }

  onScrollMeasure(): void {
    this.callAll("onScrollMeasure");
  }

  onMouseMoveMeasure(): void {
    this.callAll("onMouseMoveMeasure");
  }

  onScroll(): void {
    this.callAll("onScroll");
  }

  onResizeWidth(): void {
    this.callAll("onResizeWidth");
  }

  onResize(): void {
    this.callAll("onResize");
  }

  onRebuild(): void {
    this.callAll("onRebuild");
  }

  onMouseMove(e: MouseEvent): void {
    this.callAll("onMouseMove", e);
  }

  onWheel(e: WheelEvent): void {
    this.callAll("onWheel", e);
  }

  onDirectionChange(): void {
    this.callAll("onDirectionChange");
  }

  onScrollStart(): void {
    this.callAll("onScrollStart");
  }

  onScrollStop(): void {
    this.callAll("onScrollStop");
  }

  onAxisChange(): void {
    this.callAll("onAxisChange");
  }

  onDeviceChange(): void {
    this.callAll("onDeviceChange");
  }

  onScrollConfigChange(): void {
    this.callAll("onScrollConfigChange");
  }

  onSettingsChange(_data: ISettingsChangeData): void {
    this.callAll("onSettingsChange");
  }

  onDOMMutate(added: NodeList, removed: NodeList): void {
    this.callAll("onDOMMutate", added, removed);
  }

  destroy(): void {
    this.callAll("onUnsubscribe");
    this.callAll("destroy");
    this.modules = [];
    this.uiModules = [];
    this.allModules = [];
  }

  get all(): IStringModule[] {
    return this.allModules;
  }

  get core(): IStringModule[] {
    return this.modules;
  }

  get ui(): IStringModule[] {
    return this.uiModules;
  }

  private callAll(lifecycle: LifecycleName, arg?: unknown, arg2?: unknown): void {
    if (this.modules.length > 0) {
      this.callLifecycleStrict(this.modules, lifecycle, arg, arg2);
    }
    if (this.uiModules.length > 0) {
      this.callLifecycleStrict(this.uiModules, lifecycle, arg, arg2);
    }
  }

  private callLifecycleStrict(
    modules: StringModule[],
    lifecycle: LifecycleName,
    arg?: unknown,
    arg2?: unknown
  ): void {
    const len = modules.length;

    switch (lifecycle) {
      case "onFrame":
      case "onMutate":
      case "onScrollMeasure":
      case "onMouseMoveMeasure":
      case "onScroll":
        // Single argument: StringData
        for (let i = 0; i < len; i++) {
          const mod = modules[i];
          if (mod) (mod as any)[lifecycle](this.data);
        }
        break;

      case "onDOMMutate":
        // Two arguments: addedNodes, removedNodes
        for (let i = 0; i < len; i++) {
          const mod = modules[i];
          if (mod) (mod as any)[lifecycle](arg, arg2);
        }
        break;

      case "onMouseMove":
      case "onWheel":
        // Single argument: Event
        for (let i = 0; i < len; i++) {
          const mod = modules[i];
          if (mod) (mod as any)[lifecycle](arg);
        }
        break;

      default:
        // No arguments
        for (let i = 0; i < len; i++) {
          const mod = modules[i];
          if (mod) (mod as any)[lifecycle]();
        }
        break;
    }
  }

  private rebuildAllModules(): void {
    this.allModules.length = 0;
    for (let i = 0; i < this.modules.length; i++) {
      this.allModules.push(this.modules[i]);
    }
    for (let i = 0; i < this.uiModules.length; i++) {
      this.allModules.push(this.uiModules[i]);
    }
  }
}
